(() => {
  // Each flavor exists as three variants at different price points. The active
  // tier comes from `window.__pbPizzaTier`, set by the Intelligems experiment
  // before this script runs; anything unset or unknown falls back to the current
  // live behaviour (20% off), so the page is unchanged when no test is running.
  const PIZZA_TIERS = { base: 2300, d20: 1840, d30: 1610 };
  const DEFAULT_PIZZA_TIER = 'd20';

  const PIZZA_CATALOG = [
    {
      title: 'Margherita',
      handle: 'poco-bero-neapolitan-margherita-pizza',
      variants: { base: 47811186557105, d20: 47780061380785, d30: 47811186589873 }
    },
    {
      title: 'Garlic Pesto',
      handle: 'poco-bero-neapolitan-garlic-pesto-pizza',
      variants: { base: 47811186622641, d20: 47780062003377, d30: 47811186655409 }
    },
    {
      title: 'Cacio e Pepe',
      handle: 'poco-bero-neapolitan-cacio-e-pepe-pizza',
      variants: { base: 47811186688177, d20: 47780062068913, d30: 47811186720945 }
    },
    {
      title: 'Patata e Rosmarino',
      handle: 'poco-bero-neapolitan-potato-rosemary-pizza',
      variants: { base: 47811186753713, d20: 47780062232753, d30: 47811186786481 }
    },
    {
      title: 'Rossa',
      handle: 'rossa-pizza',
      variants: { base: 47811186819249, d20: 47780062298289, d30: 47811186852017 }
    },
    {
      title: 'Friariella',
      handle: 'poco-bero-neapolitan-friariella-pizza',
      variants: { base: 47811184754865, d20: 47780062462129, d30: 47811184787633 }
    }
  ];

  // Resolved on every read rather than latched at parse time: this script is
  // `defer`red, so reading the tier once at load would lose a late assignment
  // and serve a price the visitor was not bucketed into.
  const activePizzaTier = () => (PIZZA_TIERS[window.__pbPizzaTier] ? window.__pbPizzaTier : DEFAULT_PIZZA_TIER);
  const pizzaDiscountPct = () => Math.round((1 - PIZZA_TIERS[activePizzaTier()] / PIZZA_TIERS.base) * 100);

  // Every tier's variant id, so a pizza from ANY tier is still policed by the
  // dough rules below — not just the one the current visitor was served.
  const allPizzaVariantIds = new Set(PIZZA_CATALOG.flatMap((pizza) => Object.values(pizza.variants)));

  const CONFIG = {
    sellingPlanId: 1731821745,
    pizzaRegularPrice: PIZZA_TIERS.base,
    doughVariants: {
      44781724500145: '25 x NY-Style Dough Balls',
      47872841449649: '25 x NY-Style Dough Balls',
      47872841810097: '25 x NY-Style Dough Balls',
      44899279143089: '50 x NY-Style Dough Balls',
      47214343323825: '100 x NY-Style Dough Balls'
    },
    get pizzaTier() {
      return activePizzaTier();
    },
    get pizzas() {
      const tier = activePizzaTier();
      return PIZZA_CATALOG.map((pizza) => ({
        variantId: pizza.variants[tier],
        title: pizza.title,
        handle: pizza.handle,
        fallbackPrice: PIZZA_TIERS[tier]
      }));
    }
  };

  const pizzaDetails = new Map();
  const scriptUrl = document.currentScript?.src;
  const dividerAssetUrl = scriptUrl
    ? new URL('section-divider-flames-white.svg', scriptUrl).href
    : '';

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const eligibleSubscriptionItems = (cart) => cart.items.filter((item) => {
    return CONFIG.doughVariants[item.variant_id] && !item.selling_plan_allocation;
  });

  const hasEligibleDough = (cart) => cart.items.some((item) => CONFIG.doughVariants[item.variant_id]);
  const hasPizza = (cart) => cart.items.some((item) => item.sku?.startsWith('NP11-') || item.properties?._pb_pizza_upsell);
  const discountedPizzaItems = (cart) => cart.items.filter((item) => allPizzaVariantIds.has(item.variant_id));
  const visibleCartLines = () => Array.from(document.querySelectorAll('#cart-drawer ul[role="list"] > li'));
  const renderedCartItems = (cart) => cart.items
    .filter((item) => item.variant_id !== 45077724594353)
    .filter((item) => {
      if (!item.properties?._bundle || item.properties?._bundle_main) return true;
      return !cart.items.some((candidate) => {
        return candidate.properties?._bundle === item.properties._bundle
          && candidate.properties?._bundle_main;
      });
    });

  const applyPizzaLineLabels = (cart) => {
    const lines = visibleCartLines().filter((line) => !line.matches('[data-pb-pizza-upsell]'));

    renderedCartItems(cart).forEach((item, index) => {
      if (!discountedPizzaItems({ items: [item] }).length) return;
      const line = lines[index];
      if (!line) return;
      const titleLink = line.querySelector('a.block.leading-tight');
      const pizzaTitle = CONFIG.pizzas.find((pizza) => pizza.variantId === item.variant_id)?.title;

      const displayTitle = item.properties._pb_display_title
        || (pizzaTitle ? `${pizzaTitle} Pizza` : '')
        || item.product_title
        || item.title.replace(/\s+-\s+6 Pack$/i, '');
      if (titleLink && titleLink.textContent !== displayTitle) titleLink.textContent = displayTitle;

      line.classList.add('pb-pizza-upsell-line');
      const quantityInput = line.querySelector('input');
      if (!quantityInput) return;

      quantityInput.value = '1';
      quantityInput.readOnly = true;
      quantityInput.min = '1';
      quantityInput.max = '1';
      quantityInput.setAttribute('aria-label', 'Pizza upsell quantity fixed at 1');
      quantityInput.parentElement?.querySelectorAll('button').forEach((control) => {
        control.hidden = true;
        control.disabled = true;
        control.setAttribute('aria-hidden', 'true');
      });
    });
  };

  let isEnforcingPizzaRules = false;
  const enforcePizzaUpsellRules = async (cart) => {
    if (isEnforcingPizzaRules) return false;

    const pizzas = discountedPizzaItems(cart);
    if (!pizzas.length) return false;

    const doughIsPresent = hasEligibleDough(cart);
    const updates = {};
    pizzas.forEach((item, index) => {
      const allowedQuantity = doughIsPresent && index === 0 ? 1 : 0;
      if (item.quantity !== allowedQuantity) updates[item.key] = allowedQuantity;
    });
    if (!Object.keys(updates).length) return false;

    isEnforcingPizzaRules = true;
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error(`Pizza rule enforcement failed: ${response.status}`);
      document.dispatchEvent(new CustomEvent('update-cart'));
      return true;
    } catch (error) {
      console.error('[Poco Bero] Pizza upsell rule enforcement failed', error);
      return false;
    } finally {
      isEnforcingPizzaRules = false;
    }
  };

  const makeSubscriptionCard = (item) => {
    const card = document.createElement('div');
    card.className = 'pb-subscription-upsell';
    card.dataset.pbSubscriptionUpsell = item.key;
    card.innerHTML = `
      <div class="pb-subscription-upsell__copy">
        <div class="pb-subscription-upsell__title">Save 15% every delivery</div>
        <p class="pb-subscription-upsell__subtitle">Skip or cancel anytime.</p>
      </div>
      <button type="button" class="pb-subscription-upsell__button">Switch to subscribe</button>
      <p class="pb-subscription-upsell__fineprint" role="status" aria-live="polite"></p>
    `;
    card.querySelector('button').addEventListener('click', () => upgradeItem(item, card));
    return card;
  };

  async function upgradeItem(item, card) {
    if (!item?.key || card?.classList.contains('is-loading')) return;
    card.classList.add('is-loading');
    const button = card.querySelector('button');
    if (button) button.textContent = 'Switching...';

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: item.key,
          quantity: item.quantity,
          selling_plan: CONFIG.sellingPlanId
        })
      });
      if (!response.ok) throw new Error(`Cart swap failed: ${response.status}`);
      document.dispatchEvent(new CustomEvent('update-cart'));
    } catch (error) {
      console.error('[Poco Bero] Subscription upsell failed', error);
      card.classList.remove('is-loading');
      if (button) button.textContent = 'Switch to subscribe';
      const fineprint = card.querySelector('.pb-subscription-upsell__fineprint');
      if (fineprint) fineprint.textContent = 'Could not switch automatically. Please try again.';
    }
  }

  const loadPizzaDetails = async (pizza) => {
    if (pizzaDetails.has(pizza.variantId)) return pizzaDetails.get(pizza.variantId);

    const details = {
      image: '',
      price: pizza.fallbackPrice,
      available: true
    };

    try {
      const response = await fetch(`/products/${pizza.handle}.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Product fetch failed: ${response.status}`);
      const product = await response.json();
      const variant = product.variants?.find((candidate) => Number(candidate.id) === pizza.variantId);
      details.image = product.featured_image || product.images?.[0] || '';
      details.price = variant?.price ?? details.price;
      details.available = variant?.available ?? details.available;
    } catch (_) {
      details.available = true;
    }

    pizzaDetails.set(pizza.variantId, details);
    return details;
  };

  const renderPizzaButton = (button, price, available = true) => {
    if (!button) return;

    const regularPrice = formatMoney(CONFIG.pizzaRegularPrice);
    const offerPrice = formatMoney(price);
    button.dataset.readyLabel = `Add my pizza — ${offerPrice}`;
    button.dataset.readyPrice = price;
    button.disabled = !available;

    if (!available) {
      button.removeAttribute('aria-label');
      button.textContent = 'Sold out';
      return;
    }

    const isDiscounted = price < CONFIG.pizzaRegularPrice;
    button.setAttribute('aria-label', isDiscounted
      ? `Add my pizza. Regular price ${regularPrice}. Sale price ${offerPrice}.`
      : `Add my pizza. Price ${offerPrice}.`);
    button.innerHTML = `
      <span>Add my pizza</span>
      <span aria-hidden="true">-</span>
      <span class="pb-pizza-upsell__button-prices" aria-hidden="true">
        ${isDiscounted ? `<span class="pb-pizza-upsell__button-compare">${regularPrice}</span>` : ''}
        <span>${offerPrice}</span>
      </span>
    `;
  };

  const updatePizzaCard = async (card, pizza) => {
    const image = card.querySelector('.pb-pizza-upsell__image');
    const button = card.querySelector('.pb-pizza-upsell__button');
    const details = await loadPizzaDetails(pizza);

    if (!card.isConnected) return;
    if (image) {
      image.src = details.image;
      image.alt = `${pizza.title} pizza`;
      image.hidden = !details.image;
    }
    if (button) {
      renderPizzaButton(button, details.price, details.available);
    }
  };

  const addPizza = async (card) => {
    if (card.classList.contains('is-loading')) return;
    const select = card.querySelector('.pb-pizza-upsell__select');
    const button = card.querySelector('.pb-pizza-upsell__button');
    const status = card.querySelector('.pb-pizza-upsell__status');
    const pizza = CONFIG.pizzas.find((candidate) => candidate.variantId === Number(select?.value));
    if (!pizza) return;

    card.classList.add('is-loading');
    if (button) button.textContent = 'Adding...';
    if (status) status.textContent = '';

    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(`Cart eligibility check failed: ${cartResponse.status}`);
      const currentCart = await cartResponse.json();
      if (!hasEligibleDough(currentCart)) {
        card.classList.remove('is-loading');
        if (button) renderPizzaButton(button, Number(button.dataset.readyPrice));
        if (status) status.textContent = 'Add dough to your cart to unlock this offer.';
        return;
      }
      if (hasPizza(currentCart)) {
        document.dispatchEvent(new CustomEvent('update-cart'));
        return;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: pizza.variantId,
            quantity: 1,
            properties: {
              _pb_pizza_upsell: 'Dough order',
              _pb_upsell_source: 'mini-cart',
              _pb_display_title: `${pizza.title} Pizza`
            }
          }]
        })
      });
      if (!response.ok) throw new Error(`Pizza add failed: ${response.status}`);
      document.dispatchEvent(new CustomEvent('update-cart'));
    } catch (error) {
      console.error('[Poco Bero] Pizza upsell failed', error);
      card.classList.remove('is-loading');
      if (button) {
        const readyPrice = Number(button.dataset.readyPrice);
        if (readyPrice) {
          renderPizzaButton(button, readyPrice);
        } else {
          button.textContent = button.dataset.readyLabel || 'Add my pizza';
        }
      }
      if (status) status.textContent = 'Could not add the pizza. Please try again.';
    }
  };

  const makePizzaCard = () => {
    const card = document.createElement('li');
    const firstPizza = CONFIG.pizzas[0];
    const discountPct = pizzaDiscountPct();
    card.className = 'pb-pizza-upsell';
    card.dataset.pbPizzaUpsell = 'true';
    card.innerHTML = `
      <div class="pb-pizza-upsell__divider pb-pizza-upsell__divider--top" aria-hidden="true">
        ${dividerAssetUrl ? `<img src="${dividerAssetUrl}" alt="" width="740" height="46">` : ''}
      </div>
      <div class="pb-pizza-upsell__content">
        <div class="pb-pizza-upsell__header">
          <h3 class="pb-pizza-upsell__title">Handmade in Naples, Italy</h3>
          <p class="pb-pizza-upsell__offer">
            <strong>Free shipping</strong> with your dough${discountPct > 0 ? ` <strong>+ ${discountPct}% off</strong>` : ''}
          </p>
        </div>
        <div class="pb-pizza-upsell__product">
          <div class="pb-pizza-upsell__media">
            <img class="pb-pizza-upsell__image" src="" alt="" width="88" height="88" hidden>
          </div>
          <div class="pb-pizza-upsell__controls">
            <label class="pb-pizza-upsell__label" for="pb-pizza-upsell-flavor">Pick your pizza</label>
            <select id="pb-pizza-upsell-flavor" class="pb-pizza-upsell__select">
              ${CONFIG.pizzas.map((pizza) => `<option value="${pizza.variantId}">${pizza.title}</option>`).join('')}
            </select>
          </div>
        </div>
        <button type="button" class="pb-pizza-upsell__button" aria-label="${discountPct > 0 ? `Add my pizza. Regular price ${formatMoney(CONFIG.pizzaRegularPrice)}. Sale price ${formatMoney(firstPizza.fallbackPrice)}.` : `Add my pizza. Price ${formatMoney(firstPizza.fallbackPrice)}.`}">
          <span>Add my pizza</span>
          <span aria-hidden="true">-</span>
          <span class="pb-pizza-upsell__button-prices" aria-hidden="true">
            ${discountPct > 0 ? `<span class="pb-pizza-upsell__button-compare">${formatMoney(CONFIG.pizzaRegularPrice)}</span>` : ''}
            <span>${formatMoney(firstPizza.fallbackPrice)}</span>
          </span>
        </button>
        <p class="pb-pizza-upsell__status" role="status" aria-live="polite"></p>
      </div>
      <div class="pb-pizza-upsell__divider pb-pizza-upsell__divider--bottom" aria-hidden="true">
        ${dividerAssetUrl ? `<img src="${dividerAssetUrl}" alt="" width="740" height="46">` : ''}
      </div>
    `;

    const select = card.querySelector('.pb-pizza-upsell__select');
    select.addEventListener('change', () => {
      const pizza = CONFIG.pizzas.find((candidate) => candidate.variantId === Number(select.value));
      if (pizza) updatePizzaCard(card, pizza);
    });
    card.querySelector('.pb-pizza-upsell__button').addEventListener('click', () => addPizza(card));
    updatePizzaCard(card, firstPizza);
    return card;
  };

  const renderPizzaUpsell = (drawer, cart) => {
    const existing = drawer.querySelector('[data-pb-pizza-upsell]');
    if (!hasEligibleDough(cart) || hasPizza(cart)) {
      existing?.remove();
      return;
    }
    const list = drawer.querySelector('ul[role="list"]');
    if (existing) {
      if (list && existing !== list.lastElementChild) list.appendChild(existing);
      return;
    }

    if (list) list.appendChild(makePizzaCard());
  };

  const renderSubscriptionUpsells = (drawer, cart) => {
    const eligible = eligibleSubscriptionItems(cart);
    const eligibleKeys = new Set(eligible.map((item) => item.key));
    const renderedItems = renderedCartItems(cart);
    const lines = visibleCartLines();

    drawer.querySelectorAll('[data-pb-subscription-upsell]').forEach((node) => {
      const itemKey = node.dataset.pbSubscriptionUpsell;
      const visibleIndex = renderedItems.findIndex((item) => item.key === itemKey);
      const expectedLine = lines[visibleIndex];
      if (!eligibleKeys.has(itemKey) || node.parentElement !== expectedLine) node.remove();
    });
    if (!eligible.length) return;

    eligible.forEach((item) => {
      const visibleIndex = renderedItems.findIndex((candidate) => candidate.key === item.key);
      const line = lines[visibleIndex];
      if (!line || line.querySelector('[data-pb-subscription-upsell]')) return;
      line.appendChild(makeSubscriptionCard(item));
    });
  };

  let renderTimer;
  const renderUpsells = () => {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(async () => {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer) return;

      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        if (await enforcePizzaUpsellRules(cart)) return;
        applyPizzaLineLabels(cart);
        renderSubscriptionUpsells(drawer, cart);
        renderPizzaUpsell(drawer, cart);
      } catch (_) {
        return;
      }
    }, 80);
  };

  document.addEventListener('DOMContentLoaded', renderUpsells);
  document.addEventListener('toggle-cart', renderUpsells);
  document.addEventListener('update-cart', () => setTimeout(renderUpsells, 350));
  document.addEventListener('rebuy:cart.change', renderUpsells);

  const observer = new MutationObserver(renderUpsells);
  const startObserver = () => {
    const drawer = document.getElementById('cart-drawer');
    if (drawer) observer.observe(drawer, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startObserver);
  else startObserver();
})();
